import type { KycFileCertificationChoiceDto } from '@dealstack/shared/lib/generated';
import {
	KYCFileType,
	GetInvestmentDetailsRes,
	KycWorkflowSettingResponseDto,
	SubjectAction,
} from '@dealstack/shared/lib/generated';

import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { KycInstructionBar } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/KycInstructions/KycInstructionBar';
import { LastVerifiedBadge } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/LastVerifiedBadge';
import { useKycFileConfirmation } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/useKycFileConfirmation';
import {
	hasUnconfirmedKycFiles,
	isKycRejected,
} from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { Button, Flex, Text } from '@mantine/core';
import styled from 'styled-components';
import { ProofFileBox } from '@dealstack/frontend/investor/pages/Investment/common/ProofFileBox/ProofFileBox';
import { uploadFileV2 } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	getProofOfAddress,
	getProofOfIdentity,
	resetInheritedKycItems,
	updateProofOfAddress,
	updateProofOfIdentity,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { KycResetScope } from '@dealstack/shared/lib/common/business/kyc';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useFileUploadErrorHandler } from '@dealstack/frontend/investor/hooks/useFileUploadErrorHandler';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	nothingToSave,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { useEffect } from 'react';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { isPersonalProofCertificationRequired } from '@dealstack/shared/lib/common/business/kycCertification';
import { CertifiableProofUpload } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProofUpload';
import { ProofCertificationStatus } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/ProofCertificationStatus';
import { isCertificationRequiredFor } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProof.utils';

interface Props {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	investmentDetails: GetInvestmentDetailsRes | undefined;
	kycSetting?: KycWorkflowSettingResponseDto;
}

export const InvestmentLeavePersonalIDs = ({
	setIsContinueDisabled,
	kycSetting,
	investmentDetails,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const handleUploadError = useFileUploadErrorHandler();
	const { createConfirmHandler } = useKycFileConfirmation();

	const kycWorkflowId = investmentDetails?.activeKycWorkflowId ?? '';
	const isCertifiedCopiesRequested = isPersonalProofCertificationRequired(kycSetting);

	const proofOfIdentityQuery = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_KYC_PROOF_OF_IDENTITY_GET, kycWorkflowId],
		queryFn: () => getProofOfIdentity(kycWorkflowId),
		enabled: !!kycWorkflowId,
	});
	const uploadProofOfIdentityMutation = useMutation({
		mutationFn: uploadFileV2,
		onError: handleUploadError,
	});
	const updateProofOfIdentityMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateProofOfIdentity>[0]) =>
			updateProofOfIdentity(variables),
	});

	const onDropProofOfIdentity = (file: File, certification?: KycFileCertificationChoiceDto) => {
		uploadProofOfIdentityMutation.mutate(file, {
			onSuccess: ({ payload }) => {
				if (!payload) {
					return;
				}
				updateProofOfIdentityMutation.mutate(
					{ fileId: payload.id, kycWorkflowId, certification },
					{
						onSuccess: () => {
							showToastV2({ title: tInvestor('investment.toast.proof_uploaded') });
							void proofOfIdentityQuery.refetch();
						},
					}
				);
			},
		});
	};

	const onDeleteProofOfIdentity = () => {
		updateProofOfIdentityMutation.mutate(
			{ kycWorkflowId },
			{
				onSuccess: () => {
					showToastV2({ title: tInvestor('investment.toast.proof_removed') });
					void proofOfIdentityQuery.refetch();
				},
			}
		);
	};

	const proofOfAddressQuery = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_KYC_PROOF_OF_ADDRESS_GET, kycWorkflowId],
		queryFn: () => getProofOfAddress(kycWorkflowId),
		enabled: !!kycWorkflowId,
	});
	const uploadProofOfAddressMutation = useMutation({
		mutationFn: uploadFileV2,
		onError: handleUploadError,
	});
	const updateProofOfAddressMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateProofOfAddress>[0]) =>
			updateProofOfAddress(variables),
	});

	const onDropProofOfAddress = (file: File, certification?: KycFileCertificationChoiceDto) => {
		uploadProofOfAddressMutation.mutate(file, {
			onSuccess: ({ payload }) => {
				if (!payload) {
					return;
				}
				updateProofOfAddressMutation.mutate(
					{ fileId: payload.id, kycWorkflowId, certification },
					{
						onSuccess: () => {
							showToastV2({ title: tInvestor('investment.toast.proof_uploaded') });
							void proofOfAddressQuery.refetch();
						},
					}
				);
			},
		});
	};

	const onDeleteProofOfAddress = () => {
		updateProofOfAddressMutation.mutate(
			{ kycWorkflowId },
			{
				onSuccess: () => {
					showToastV2({ title: tInvestor('investment.toast.proof_removed') });
					void proofOfAddressQuery.refetch();
				},
			}
		);
	};

	const proofOfIdentityDetails = proofOfIdentityQuery.data?.payload;
	const proofOfAddressDetails = proofOfAddressQuery.data?.payload;

	const onConfirmProofOfIdentity = createConfirmHandler(
		proofOfIdentityDetails?.id,
		() => void proofOfIdentityQuery.refetch()
	);
	const onConfirmProofOfAddress = createConfirmHandler(
		proofOfAddressDetails?.id,
		() => void proofOfAddressQuery.refetch()
	);

	useEffect(() => {
		const isAllUploaded = proofOfIdentityDetails && proofOfAddressDetails;
		const isNotRejected =
			!isKycRejected(proofOfIdentityDetails?.status) &&
			!isKycRejected(proofOfAddressDetails?.status);
		const hasUnconfirmed = hasUnconfirmedKycFiles([
			proofOfIdentityDetails,
			proofOfAddressDetails,
		]);

		setIsContinueDisabled(!isAllUploaded || !isNotRejected || hasUnconfirmed);
	}, [proofOfIdentityDetails, proofOfAddressDetails]);

	useWizardStep(nothingToSave);

	const isLoading = proofOfIdentityQuery.isLoading || proofOfAddressQuery.isLoading;

	const hasInheritedItems =
		proofOfIdentityDetails?.subjectAction != null ||
		proofOfAddressDetails?.subjectAction != null;

	const hasUnconfirmedInherited =
		proofOfIdentityDetails?.subjectAction === SubjectAction.NOT_YET_ACTED ||
		proofOfAddressDetails?.subjectAction === SubjectAction.NOT_YET_ACTED;
	const hasAnyFiles = !!proofOfIdentityDetails || !!proofOfAddressDetails;
	const showReset = hasAnyFiles && !hasUnconfirmedInherited;

	const onResetAll = async () => {
		await resetInheritedKycItems(kycWorkflowId, KycResetScope.PROOF);
		void proofOfIdentityQuery.refetch();
		void proofOfAddressQuery.refetch();
	};

	return (
		<Flex maw="40rem" direction="column" gap="2rem">
			{isLoading && <InlineLoader />}
			{hasInheritedItems && (
				<Flex direction="column" gap="0.25rem">
					<Text fw={700}>{tInvestor('investment.join.kyc.inherited.title')}</Text>
					<Text size="sm" c="dark.0">
						{tInvestor('investment.join.kyc.inherited.description')}
					</Text>
				</Flex>
			)}
			<ProofWrapper>
				<Flex align="center" gap="0.5rem">
					<Text c="dark.0">{tInvestor('investment.join.kyc.proof_id')}</Text>
					{proofOfIdentityDetails?.subjectAction != null &&
						proofOfIdentityDetails?.createdAt && (
							<LastVerifiedBadge date={proofOfIdentityDetails.createdAt} />
						)}
				</Flex>
				{!!proofOfIdentityDetails && (
					<ProofFileBox
						fileName={proofOfIdentityDetails.fileName}
						fileId={proofOfIdentityDetails.fileId}
						onDelete={onDeleteProofOfIdentity}
						onConfirm={onConfirmProofOfIdentity}
						subjectAction={proofOfIdentityDetails.subjectAction}
						rejectionComment={proofOfIdentityDetails.rejectionComment}
						isRejected={isKycRejected(proofOfIdentityDetails.status)}
						date={proofOfIdentityDetails.createdAt}
						certificationSlot={
							<ProofCertificationStatus
								kycFile={proofOfIdentityDetails}
								certificationRequired={isCertificationRequiredFor(
									kycSetting,
									KYCFileType.PROOF_OF_IDENTITY
								)}
								isRejected={isKycRejected(proofOfIdentityDetails.status)}
								onChanged={() => void proofOfIdentityQuery.refetch()}
							/>
						}
					/>
				)}
				{!proofOfIdentityDetails && (
					<CertifiableProofUpload
						certificationRequired={isCertificationRequiredFor(
							kycSetting,
							KYCFileType.PROOF_OF_IDENTITY
						)}
						onDropFile={onDropProofOfIdentity}
						loading={uploadProofOfIdentityMutation.isPending}
					/>
				)}
			</ProofWrapper>

			<ProofWrapper>
				<Flex align="center" gap="0.5rem">
					<Text c="dark.0">{tInvestor('investment.join.kyc.proof_address')}</Text>
					{proofOfAddressDetails?.subjectAction != null &&
						proofOfAddressDetails?.createdAt && (
							<LastVerifiedBadge date={proofOfAddressDetails.createdAt} />
						)}
				</Flex>
				{!!proofOfAddressDetails && (
					<ProofFileBox
						fileName={proofOfAddressDetails.fileName}
						fileId={proofOfAddressDetails.fileId}
						onDelete={onDeleteProofOfAddress}
						onConfirm={onConfirmProofOfAddress}
						subjectAction={proofOfAddressDetails.subjectAction}
						rejectionComment={proofOfAddressDetails.rejectionComment}
						isRejected={isKycRejected(proofOfAddressDetails.status)}
						date={proofOfAddressDetails.createdAt}
						certificationSlot={
							<ProofCertificationStatus
								kycFile={proofOfAddressDetails}
								certificationRequired={isCertificationRequiredFor(
									kycSetting,
									KYCFileType.PROOF_OF_ADDRESS
								)}
								isRejected={isKycRejected(proofOfAddressDetails.status)}
								onChanged={() => void proofOfAddressQuery.refetch()}
							/>
						}
					/>
				)}
				{!proofOfAddressDetails && (
					<CertifiableProofUpload
						certificationRequired={isCertificationRequiredFor(
							kycSetting,
							KYCFileType.PROOF_OF_ADDRESS
						)}
						onDropFile={onDropProofOfAddress}
						loading={uploadProofOfAddressMutation.isPending}
					/>
				)}
			</ProofWrapper>

			{showReset && (
				<Flex>
					<Button
						size="sm"
						variant="outline"
						color="dark"
						onClick={onResetAll}
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_DISMISS_BUTTON}
					>
						{tInvestor('investment.join.kyc.inherited.reset')}
					</Button>
				</Flex>
			)}

			<KycInstructionBar isCertifiedCopiesOfDocumentsRequested={isCertifiedCopiesRequested} />
		</Flex>
	);
};

const ProofWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.5rem;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[4]};
	border-radius: 4px;
`;
